import { type ReactNode, use, useId, useLayoutEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { createStore, useStore } from "zustand";

import { EditToolbar } from "./EditToolbar";
import { SectionsContext, type SectionsStore } from "./sections";

type EditSectionsProps = {
  /** Names what Save saves, e.g. "Save group". The innermost one wins. */
  saveLabel: string;
  children: ReactNode;
};

/**
 * Gives everything below it one permanent edit toolbar. Each part of the page
 * registers what it edits with `useEditSection`, and Save and Discard act on
 * every part with changes. Inside another `EditSections` it only names Save,
 * so shared editors can wrap themselves and still share their page's toolbar.
 */
export function EditSections({ saveLabel, children }: EditSectionsProps) {
  const parent = use(SectionsContext);
  const [ownStore] = useState<SectionsStore>(() =>
    createStore(() => ({ sections: {}, saveLabels: {} })),
  );
  const store = parent?.store ?? ownStore;
  const depth = parent ? parent.depth + 1 : 0;
  const id = useId();

  useLayoutEffect(() => {
    store.setState((state) => ({
      saveLabels: { ...state.saveLabels, [id]: { depth, label: saveLabel } },
    }));
    return () =>
      store.setState((state) => {
        const saveLabels = { ...state.saveLabels };
        delete saveLabels[id];
        return { saveLabels };
      });
  }, [store, id, depth, saveLabel]);

  return (
    <SectionsContext value={{ store, depth }}>
      {parent ? null : <SectionsToolbar store={store} />}
      {children}
    </SectionsContext>
  );
}

function SectionsToolbar({ store }: { store: SectionsStore }) {
  const { t } = useTranslation();
  const sections = Object.values(useStore(store, (state) => state.sections));
  const saveLabels = Object.values(
    useStore(store, (state) => state.saveLabels),
  );
  const changed = sections.filter((section) => section.changed);
  const saveLabel = saveLabels.reduce<{ depth: number; label?: string }>(
    (deepest, entry) => (entry.depth >= deepest.depth ? entry : deepest),
    { depth: -1 },
  ).label;

  return (
    <EditToolbar
      changed={changed.length > 0}
      changes={changed.flatMap((section) =>
        section.changes.length
          ? section.changes.map((change) => ({
              ...change,
              category: change.category ?? section.title,
            }))
          : [
              {
                category: section.title,
                text: t("generic.editToolbar.changes.other"),
              },
            ],
      )}
      saving={sections.some((section) => section.saving)}
      onSave={() => changed.forEach((section) => section.save())}
      onDiscard={() => changed.forEach((section) => section.discard?.())}
      saveLabel={saveLabel}
      actions={sections.flatMap((section) => section.actions)}
    />
  );
}
